import { useEffect, useRef, useState } from "react";
import type { PlatformApiClient } from "../api";
import type { OrganizationContext } from "../types";
import { DemoModuleRuntime } from "./DemoModuleRuntime";
import {
  demoDefinitionById,
  isDemoModuleKey,
  type DemoModuleKey,
} from "./definitions";

export function DemoDetailWorkspace({
  demoId,
  moduleKey,
  nestedPath,
  organizations,
  api,
  navigate,
  onExpired,
}: {
  demoId: string;
  moduleKey: string | null;
  nestedPath: string;
  organizations: readonly OrganizationContext[];
  api: PlatformApiClient;
  navigate: (path: string) => void;
  onExpired: () => void;
}) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [demoId, moduleKey, nestedPath]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setMobileNavOpen(false);
      window.requestAnimationFrame(() => mobileNavTriggerRef.current?.focus());
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  const definition = demoDefinitionById(demoId);
  if (!definition) {
    return <main className="demo-presentation-missing" id="main-content" tabIndex={-1}>
      <div>
        <p className="eyebrow">Demos</p>
        <h1>Demo no encontrada</h1>
        <p>Esta demo no existe en el catálogo interno.</p>
        <button className="primary-button" onClick={() => navigate("/demos")}>
          Volver al catálogo
        </button>
      </div>
    </main>;
  }

  const selectedKey: DemoModuleKey =
    moduleKey &&
    isDemoModuleKey(moduleKey) &&
    definition.modules.some((module) => module.key === moduleKey)
      ? moduleKey
      : definition.modules[0]?.key ?? "home";

  const manifest = definition.manifest;
  function openModule(path: string) {
    setMobileNavOpen(false);
    navigate(path);
  }

  return <div className="demo-presentation">
    <header className="demo-presentation__topbar">
      <button
        type="button"
        className="demo-presentation__brand"
        onClick={() => navigate(`/demos/${encodeURIComponent(manifest.id)}`)}
        aria-label={`Inicio de ${manifest.name}`}
      >
        <span className="demo-presentation__brand-mark" aria-hidden="true">
          {manifest.name.slice(0, 1).toUpperCase()}
        </span>
        <span>
          <strong>{manifest.name}</strong>
          <small>{manifest.solutionLabel}</small>
        </span>
      </button>

      <div className="demo-presentation__topbar-actions">
        <button
          ref={mobileNavTriggerRef}
          type="button"
          className="demo-presentation__mobile-nav-trigger"
          aria-label="Abrir módulos de la demo"
          aria-controls="demo-mobile-navigation"
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((current) => !current)}
        >
          <span aria-hidden="true">☰</span>
          <span>Módulos</span>
        </button>
        <button
          type="button"
          className="demo-presentation__exit"
          onClick={() => navigate("/demos")}
          aria-label="Volver al catálogo de demos"
        >
          <span aria-hidden="true">←</span>
          <span className="demo-presentation__exit-label">Volver al catálogo</span>
        </button>
      </div>
    </header>

    <div className="demo-presentation__body">
      {mobileNavOpen && <button
        type="button"
        className="demo-presentation__mobile-backdrop"
        aria-label="Cerrar navegación de módulos"
        onClick={() => {
          setMobileNavOpen(false);
          window.requestAnimationFrame(() => mobileNavTriggerRef.current?.focus());
        }}
      />}
      <aside
        id="demo-mobile-navigation"
        className={"demo-presentation__sidebar" + (mobileNavOpen ? " demo-presentation__sidebar--open" : "")}
        aria-label="Navegación de la demo"
      >
        <div className="demo-presentation__sidebar-heading">
          <span>Mi negocio</span>
          <strong>{manifest.industryLabel}</strong>
        </div>

        <nav className="demo-presentation__nav" aria-label="Módulos de la demo">
          {definition.modules.map((module) => {
            const path = `/demos/${encodeURIComponent(manifest.id)}/${module.key}`;
            return <button
              key={module.key}
              type="button"
              aria-current={module.key === selectedKey ? "page" : undefined}
              onClick={() => openModule(path)}
            >
              <span>{module.label}</span>
            </button>;
          })}
        </nav>

        <div className="demo-presentation__sidebar-footer">
          <span className="demo-presentation__live-dot" aria-hidden="true" />
          <div>
            <strong>{manifest.name}</strong>
            <small>{manifest.launch.kind === "unavailable" ? "En preparación" : "Referencia de demostración"}</small>
          </div>
        </div>
      </aside>

      <main
        id="main-content"
        className="demo-presentation__content"
        tabIndex={-1}
      >
        <DemoModuleRuntime
          definition={definition}
          moduleKey={selectedKey}
          nestedPath={nestedPath}
          organizations={organizations}
          api={api}
          navigate={navigate}
          onExpired={onExpired}
        />
      </main>
    </div>
  </div>;
}
